import { useMemo } from 'react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Card, CardContent } from '@/components/ui/card'
import { usePlanMetadata } from '@/hooks/usePlanMetadata'
import { useCurrentVersionId } from '@/hooks/useCurrentVersionId'
import { buildPlanFinancialsRows, formatMetric } from './planFinancialsMockData'

interface PlanSummaryTabProps {
  planId: string
}

function formatDate(value: string | null | undefined): string {
  if (!value) return '-'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return '-'
  return d.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: '2-digit',
    year: 'numeric',
  })
}

function formatTimestamp(value: Date): string {
  const date = value.toLocaleDateString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })
  const time = value.toLocaleTimeString('en-US', {
    hour12: false,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
  return `${date} ${time}`
}

function statusLabel(status: string | undefined): string {
  if (!status) return '-'
  return status.replace(/_/g, ' ').toUpperCase()
}

export function PlanSummaryTab({ planId }: PlanSummaryTabProps) {
  const { data: plan } = usePlanMetadata(planId)
  const { versionId } = useCurrentVersionId(planId)

  const currentVersion = useMemo(
    () => plan?.versions.find(v => v.id === versionId) ?? plan?.versions[0],
    [plan, versionId]
  )

  const rows = useMemo(() => buildPlanFinancialsRows(planId), [planId])

  const dataAsOf = useMemo(() => formatTimestamp(new Date()), [])

  const planTitle = plan?.name ?? ''

  return (
    <div className="flex flex-col gap-4">
      {/* Title strip */}
      <div className="flex flex-col">
        <span className="text-xs italic text-destructive">
          Assortment Reports
        </span>
        <h2 className="text-2xl font-semibold text-foreground">
          Financial Summary
        </h2>
      </div>

      {/* Two-column layout */}
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
        {/* Left: Financial Summary table */}
        <div className="flex-1">
          <Card className="overflow-hidden">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="w-[55%] border-l-4 border-l-[#84C4B4]" />
                    <TableHead className="text-right text-xs font-semibold uppercase tracking-wide text-foreground">
                      Assortment
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map(row => (
                    <TableRow key={row.label}>
                      <TableCell className="border-l-4 border-l-[#84C4B4] bg-[#fafafa] font-medium text-foreground">
                        {row.label}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {row.assortment === null
                          ? '-'
                          : formatMetric(row.assortment, row.format)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>

        {/* Right: Plan info column */}
        <div className="flex w-full flex-col gap-3 lg:w-[360px] lg:shrink-0">
          <div className="text-right text-[10px] text-muted-foreground">
            data as of: {dataAsOf}
          </div>

          <Card>
            <CardContent className="p-5">
              <h3 className="text-center text-base font-semibold leading-snug text-foreground">
                {planTitle}
              </h3>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="grid grid-cols-3 gap-4 border-b border-border pb-2 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                <span>Status</span>
                <span>Version #</span>
                <span>Version Alias</span>
              </div>
              <div className="grid grid-cols-3 gap-4 pt-2 text-sm font-medium text-foreground">
                <span>{statusLabel(plan?.status)}</span>
                <span>{currentVersion?.version_number ?? '-'}</span>
                <span className="truncate">{currentVersion?.alias ?? '-'}</span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="grid grid-cols-[1fr_1.4fr_1.4fr] gap-4 border-b border-border pb-2 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                <span>Season Event</span>
                <span>Set/Reset date</span>
                <span>Exit date</span>
              </div>
              <div className="grid grid-cols-[1fr_1.4fr_1.4fr] gap-4 pt-2 text-sm font-medium text-foreground">
                <span className="truncate">{plan?.season?.name ?? '-'}</span>
                <span className="truncate">
                  {formatDate(plan?.set_reset_date)}
                </span>
                <span className="truncate">{formatDate(plan?.exit_date)}</span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                Notes
              </span>
              <div className="mt-2 border-b border-border" />
              <p className="mt-2 min-h-[60px] whitespace-pre-wrap break-words text-sm text-foreground">
                {plan?.notes?.trim() || ''}
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
